import { EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS } from "@repo/caip";
import { z } from "zod";

const canonicalAccount = z.string().regex(/^eip155:[1-9]\d*:0x[0-9a-f]{40}$/u);
const canonicalAsset = z
  .string()
  .regex(/^eip155:[1-9]\d*\/(?:erc20:0x[0-9a-f]{40}|slip44:60)$/u)
  .refine(
    (value) =>
      !value.endsWith(`/erc20:${EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS}`),
  );
const positiveUint256 = z
  .string()
  .regex(/^[1-9]\d*$/u)
  .max(78)
  .refine((value) => {
    try {
      return BigInt(value) < 1n << 256n;
    } catch {
      return false;
    }
  });

/** Server-owned recipient and fee transfers, persisted and executed atomically. */
export const ExternalWithdrawalBatchArgsSchema = z
  .object({
    executionAccountRef: canonicalAccount,
    transfers: z
      .array(
        z
          .object({
            assetId: canonicalAsset,
            amountRaw: positiveUint256,
            recipient: z
              .string()
              .regex(/^0x[0-9a-fA-F]{40}$/u)
              .refine(
                (value) =>
                  value !== "0x0000000000000000000000000000000000000000",
              )
              .transform((value) => value.toLowerCase()),
          })
          .strict(),
      )
      .min(1)
      .max(51),
  })
  .strict()
  .superRefine((args, ctx) => {
    const chainRef = args.executionAccountRef.slice(
      0,
      args.executionAccountRef.lastIndexOf(":"),
    );
    const chainId = Number(chainRef.slice("eip155:".length));
    if (!Number.isSafeInteger(chainId) || chainId <= 0) {
      ctx.addIssue({
        code: "custom",
        path: ["executionAccountRef"],
        message: "Execution account must use a safe canonical EVM chain",
      });
    }
    let previous: string | undefined;
    const totals = new Map<string, bigint>();
    for (const [index, transfer] of args.transfers.entries()) {
      if (!transfer.assetId.startsWith(`${chainRef}/`)) {
        ctx.addIssue({
          code: "custom",
          path: ["transfers", index, "assetId"],
          message: "Every transfer must use the execution account chain",
        });
      }
      const identity = `${transfer.assetId}\0${transfer.recipient}`;
      if (previous !== undefined && previous >= identity) {
        ctx.addIssue({
          code: "custom",
          path: ["transfers", index],
          message: "Transfers must be sorted and unique by asset and recipient",
        });
      }
      previous = identity;
      // Refinements can still run after a malformed amount; do not throw here.
      if (positiveUint256.safeParse(transfer.amountRaw).success) {
        const total =
          (totals.get(transfer.assetId) ?? 0n) + BigInt(transfer.amountRaw);
        totals.set(transfer.assetId, total);
        if (total >= 1n << 256n) {
          ctx.addIssue({
            code: "custom",
            path: ["transfers", index, "amountRaw"],
            message: "Total debit for an asset must fit uint256",
          });
        }
      }
    }
  });

export type ExternalWithdrawalBatchArgs = z.infer<
  typeof ExternalWithdrawalBatchArgsSchema
>;
